import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { VITE_BACKEND_SOCKET_IO } from '../config/config';
import { IconCheck, IconRefresh, IconAlertCircle, IconExternalLink, IconCopy } from '@tabler/icons-react';
import toast from 'react-hot-toast';

export default function PaymentQRDisplay({ paymentType, amount, orderId, onSuccess }) {
  const [qrString, setQrString] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [providerInfo, setProviderInfo] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [qrMode, setQrMode] = useState('static'); // 'static' or 'dynamic'

  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const initQR = async () => {
    setLoading(true);
    setError(null);
    setIsSuccess(false);

    if (!paymentType?.qr_config) {
      setLoading(false);
      setError("No QR configuration found for this payment method.");
      return;
    }

    try {
      let config;
      try {
        config = typeof paymentType.qr_config === 'string' 
          ? JSON.parse(paymentType.qr_config) 
          : paymentType.qr_config;
      } catch (e) {
        // Plain string fallback (legacy or direct URL)
        setQrMode('static');
        setQrString(paymentType.qr_config);
        setLoading(false);
        return;
      }

      if (!config) {
        setError("Invalid QR configuration");
        setLoading(false);
        return;
      }

      if (config.type === 'static' || !config.type) {
        setQrMode('static');
        const link = config.qr_string || config.link || paymentType.qr_config;
        if (!link) {
          setError("Static QR string is empty. Please configure in Payment Types settings.");
        } else {
          setQrString(link);
        }
        setLoading(false);
        return;
      }

      if (config.type === 'dynamic') {
        setQrMode('dynamic');
        setProviderInfo(config.provider || 'fonepay');

        const token = localStorage.getItem('__acct');
        const res = await axios.post(`${VITE_BACKEND_SOCKET_IO}/api/v1/payments/generate-dynamic-qr`, {
          payment_type_id: paymentType.id,
          amount: amount,
          order_id: orderId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data.success && res.data.qr_string) {
          setQrString(res.data.qr_string);
          setReferenceId(res.data.reference_id);
          setLoading(false);
        } else {
          setError(res.data.message || "Failed to generate dynamic QR from payment gateway.");
          setLoading(false);
        }
      } else {
        setError("Unsupported QR type");
        setLoading(false);
      }
    } catch (err) {
      console.error("Error initializing QR:", err);
      const msg = err.response?.data?.message || err.message || "Failed to generate QR code";
      setError(msg);
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let pollInterval = null;

    initQR();

    return () => {
      isMounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [paymentType?.id, paymentType?.qr_config, amount, orderId]);

  // Dynamic QR Status Polling
  useEffect(() => {
    if (qrMode !== 'dynamic' || !referenceId || isSuccess) return;

    let isMounted = true;
    const token = localStorage.getItem('__acct');

    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await axios.post(`${VITE_BACKEND_SOCKET_IO}/api/v1/payments/check-status`, {
          payment_type_id: paymentType.id,
          reference_id: referenceId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (statusRes.data.status === 'success') {
          clearInterval(pollInterval);
          if (isMounted) {
            setIsSuccess(true);
            toast.success("Payment Received Successfully!");
            setTimeout(() => {
              if (onSuccessRef.current) {
                onSuccessRef.current();
              }
            }, 1000);
          }
        }
      } catch (e) {
        // Suppress poll errors
      }
    }, 3000);

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
    };
  }, [qrMode, referenceId, isSuccess, paymentType?.id]);

  // Manual Check Status Button
  const handleManualCheckStatus = async () => {
    if (!referenceId || checkingStatus) return;
    setCheckingStatus(true);
    try {
      const token = localStorage.getItem('__acct');
      const statusRes = await axios.post(`${VITE_BACKEND_SOCKET_IO}/api/v1/payments/check-status`, {
        payment_type_id: paymentType.id,
        reference_id: referenceId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (statusRes.data.status === 'success') {
        setIsSuccess(true);
        toast.success("Payment Verified Successfully!");
        if (onSuccessRef.current) {
          onSuccessRef.current();
        }
      } else {
        toast(statusRes.data.message || "Payment not yet detected. Please ask customer to scan and pay.", {
          icon: '⏳'
        });
      }
    } catch (e) {
      toast.error("Failed to check status with gateway");
    } finally {
      setCheckingStatus(false);
    }
  };

  const copyQrString = () => {
    if (!qrString) return;
    navigator.clipboard.writeText(qrString);
    toast.success("Link copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-3">
        <div className="w-8 h-8 border-3 border-restro-green border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
          {paymentType?.title?.toLowerCase()?.includes('fonepay') || providerInfo === 'fonepay'
            ? "Requesting Fonepay Dynamic QR..."
            : "Generating Payment QR Code..."}
        </p>
        <span className="text-xs text-gray-400">Locking amount: Rs. {parseFloat(amount || 0).toFixed(2)}</span>
      </div>
    );
  }

  if (error || !qrString) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center space-y-3 max-w-sm">
        <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 flex items-center justify-center">
          <IconAlertCircle size={22} />
        </div>
        <p className="text-xs font-bold text-red-600 dark:text-red-400">
          {error || "Could not generate QR code"}
        </p>
        <button
          type="button"
          onClick={initQR}
          className="btn btn-xs rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:text-gray-200 flex items-center gap-1"
        >
          <IconRefresh size={14} /> Retry
        </button>
      </div>
    );
  }

  const isUrl = qrString.startsWith('http://') || qrString.startsWith('https://');

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-xs mx-auto animate-in fade-in duration-200">
      {/* Header Badge */}
      <div className="flex items-center gap-2 mb-3">
        {qrMode === 'dynamic' ? (
          <span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-red-100 dark:bg-red-950/60 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 flex items-center gap-1.5 shadow-xs">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            Fonepay Dynamic QR
          </span>
        ) : (
          <span className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shadow-xs">
            Static QR
          </span>
        )}
      </div>

      {/* QR Code Container */}
      <div className="relative p-4 bg-white rounded-2xl shadow-md border border-gray-200 dark:border-zinc-700 flex flex-col items-center">
        <QRCodeSVG 
          value={qrString} 
          size={190} 
          level="M" 
          includeMargin={true}
        />
        
        {/* Success Overlay */}
        {isSuccess && (
          <div className="absolute inset-0 bg-emerald-600/90 rounded-2xl flex flex-col items-center justify-center text-white p-4 backdrop-blur-xs animate-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-full bg-white text-emerald-600 flex items-center justify-center mb-2 shadow-lg">
              <IconCheck size={28} stroke={3} />
            </div>
            <p className="font-extrabold text-base">Payment Received!</p>
            <p className="text-xs text-emerald-100 mt-1">Completing order...</p>
          </div>
        )}
      </div>

      {/* Amount Display */}
      <div className="mt-3 text-center">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount to Pay</div>
        <div className="text-xl font-black text-emerald-600 dark:text-emerald-400">
          Rs. {parseFloat(amount || 0).toFixed(2)}
        </div>
      </div>

      {/* Instructions / Status */}
      {qrMode === 'dynamic' ? (
        <div className="mt-3 w-full space-y-2">
          {referenceId && (
            <div className="text-[11px] text-gray-400 text-center font-mono">
              Ref: {referenceId}
            </div>
          )}

          {!isSuccess && (
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 rounded-xl border border-amber-200 dark:border-amber-900/50">
                <div className="w-3.5 h-3.5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Waiting for customer payment...</span>
              </div>

              <button
                type="button"
                onClick={handleManualCheckStatus}
                disabled={checkingStatus}
                className="btn btn-xs rounded-lg border border-gray-300 dark:border-zinc-700 bg-white hover:bg-gray-50 dark:bg-zinc-800 dark:text-gray-200 text-xs font-semibold flex items-center gap-1.5 shadow-xs"
              >
                <IconRefresh size={13} className={checkingStatus ? "animate-spin" : ""} />
                {checkingStatus ? "Checking..." : "Verify Payment Now"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Scan with any mobile banking app, eSewa, Khalti, or Fonepay app.
          </p>
          {isUrl && (
            <div className="mt-2 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={copyQrString}
                className="btn btn-xs rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-[11px] font-medium flex items-center gap-1"
                title="Copy QR payment link"
              >
                <IconCopy size={13} /> Copy Link
              </button>
              <a
                href={qrString}
                target="_blank"
                rel="noreferrer"
                className="btn btn-xs rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-[11px] font-medium flex items-center gap-1 text-restro-green hover:underline"
              >
                <IconExternalLink size={13} /> Open
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
